import React, { useState, useEffect } from "react";
import api from "../../../../utils/axios";
import Pagination from "../../../components/Pagination";
import { getKeyboardInstructions } from "../../../../utils/ModalKeyboardNavigation";
import { useEscapeClose } from "../../../../utils/SamuiUtils";
import Swal from "sweetalert2";

export default function PackageModal({
  isOpen,
  onClose,
  onSelect,
  selectedIds = [],
  docId,
}) {
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEscapeClose(isOpen, () => {
    Swal.close();
    onClose();
  });

  useEffect(() => {
    if (!isOpen || !docId) return;
    setLoading(true);

    api
      .get(`/get-quote/${docId}`)
      .then((res) => {
        if (res.data.status === "OK") {
          const isVat = res.data.header?.IsVat ?? 0;
          const items = (res.data.details || []).map((item) => ({
            ...item,
            IsVat: isVat,
          }));
          setPackages(items);
        } else {
          setPackages([]);
        }
      })
      .catch(() => setPackages([]))
      .finally(() => setLoading(false));
  }, [isOpen, docId]);

  useEffect(() => {
    if (!isOpen) {
      setSelected(null);
      setSearch("");
      setCurrentPage(1);
      Swal.close();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const filtered = packages.filter(
    (p) =>
      p.Item_Name?.toLowerCase().includes(search.toLowerCase()) ||
      p.Item_Code?.toString().includes(search)
  );

  const totalItems = filtered.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedList = filtered.slice(startIndex, startIndex + itemsPerPage);

  const handleConfirmSelect = () => {
    const selectedObject = packages.find((p) => p.Item_Id === selected);
    if (!selectedObject) {
      Swal.fire("แจ้งเตือน", "กรุณาเลือกบริการก่อน", "warning").then(() =>
        Swal.close()
      );
      return;
    }

    const qty = Number(selectedObject.Item_Qty) || 0;
    const price = Number(selectedObject.Item_Price_Unit) || 0;
    const discount = Number(selectedObject.Item_Discount) || 0;
    const total = qty * price;
    const net = total - discount;
    const isVat = Number(selectedObject.IsVat) === 1 ? 1 : 0;

    onSelect({
      Item_Id: selectedObject.Item_Id,
      Item_Name: selectedObject.Item_Name,
      Item_Qty: qty,
      Item_Unit: selectedObject.Item_Unit,
      Item_Price_Unit: price,
      Item_Discount: discount,
      Item_Total: total,
      Item_Net: net,
      IsVat: isVat,
      Item_Img: selectedObject.Item_Img || null,
    });

    Swal.close();
    onClose();
  };

  const getImageSrc = (item) => {
    if (!item.Item_Img) return "";
    try {
      if (item.Item_Img?.data) {
        const blob = new Blob([Uint8Array.from(item.Item_Img.data)], {
          type: "image/jpeg",
        });
        return URL.createObjectURL(blob);
      }
      return item.Item_Img;
    } catch {
      return "";
    }
  };

  return (
    <>
      <div
        className={`modal fade ${isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content border-0 shadow-lg rounded-lg">
            <div
              className="modal-header"
              style={{ backgroundColor: "#E13F57", color: "white" }}
            >
              <h5 className="modal-title">
                <b>เลือกบริการจากใบเสนอราคา</b>
              </h5>
              <button
                type="button"
                className="close"
                onClick={() => {
                  Swal.close();
                  onClose();
                }}
              >
                <span>&times;</span>
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group mb-3 d-flex justify-between items-center">
                <input
                  type="text"
                  className="form-control"
                  placeholder="ค้นหา ชื่อบริการ / รหัสบริการ..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="table-responsive" style={{ maxHeight: "450px" }}>
                <table
                  className="table table-striped table-hover text-sm"
                  style={{ fontSize: "14px" }}
                >
                  <thead
                    style={{
                      backgroundColor: "#E13F57",
                      color: "white",
                    }}
                  >
                    <tr>
                      <th className="text-center" style={{ width: "5%" }}></th>
                      <th className="text-center" style={{ width: "12%" }}>
                        รูปบริการ
                      </th>
                      <th>ชื่อบริการ</th>
                      <th className="text-right" style={{ width: "10%" }}>
                        จำนวน
                      </th>
                      <th className="text-center" style={{ width: "10%" }}>
                        หน่วย
                      </th>
                      <th className="text-right" style={{ width: "15%" }}>
                        ราคา/หน่วย
                      </th>
                      <th className="text-right" style={{ width: "15%" }}>
                        รวม
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="7" className="text-center py-4 text-muted">
                          กำลังโหลดข้อมูล...
                        </td>
                      </tr>
                    ) : paginatedList.length > 0 ? (
                      paginatedList.map((p) => {
                        const imgSrc = getImageSrc(p);
                        return (
                          <tr
                            key={p.Item_Id}
                            onClick={() => setSelected(p.Item_Id)}
                            style={{
                              cursor: "pointer",
                              backgroundColor:
                                selected === p.Item_Id
                                  ? "rgba(225,63,87,0.15)"
                                  : "transparent",
                              transition: "background-color 0.2s ease",
                            }}
                          >
                            <td className="text-center">
                              <input
                                type="radio"
                                name="serviceSelect"
                                checked={selected === p.Item_Id}
                                onChange={() => setSelected(p.Item_Id)}
                                className="cursor-pointer accent-[#E13F57]"
                              />
                            </td>

                            <td className="text-center align-middle">
                              {imgSrc ? (
                                <img
                                  src={imgSrc}
                                  alt={p.Item_Name}
                                  style={{
                                    width: "50px",
                                    height: "50px",
                                    objectFit: "cover",
                                    borderRadius: "6px",
                                  }}
                                />
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>

                            <td className="align-middle">{p.Item_Name}</td>
                            <td className="text-right align-middle">
                              {p.Item_Qty}
                            </td>
                            <td className="text-center align-middle">
                              {p.Item_Unit}
                            </td>
                            <td className="text-right align-middle">
                              {Number(p.Item_Price_Unit || 0).toLocaleString(
                                undefined,
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              )}
                            </td>
                            <td className="text-right align-middle">
                              {Number(p.Item_Total || 0).toLocaleString(
                                undefined,
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center py-4 text-muted">
                          ไม่พบข้อมูล
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-2 border-top pt-2">
                <Pagination
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(val) => {
                    setItemsPerPage(val);
                    setCurrentPage(1);
                  }}
                />
              </div>
            </div>

            <div className="modal-footer">
              <div
                className="text-left text-muted mr-auto"
                style={{ fontSize: "12px" }}
              >
                {getKeyboardInstructions("บริการจากใบเสนอราคา", true)}
              </div>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  Swal.close();
                  onClose();
                }}
              >
                ปิด
              </button>
              <button
                onClick={handleConfirmSelect}
                disabled={!selected}
                className={`btn ${selected
                    ? "btn-primary"
                    : "btn-secondary disabled text-gray-400"
                  }`}
                style={{
                  backgroundColor: selected ? "#E13F57" : "#ccc",
                  borderColor: selected ? "#E13F57" : "#ccc",
                }}
              >
                เลือก
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
